import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, AlertCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function ActivateAccount() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, reason: "", email: "", name: "" });
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, reason: "Aucun token fourni" }); return; }
    api.get(`/activate/check?token=${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, valid: false, reason: "Erreur de vérification du lien" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Mot de passe trop court (6 caractères minimum)"); return; }
    if (pwd !== confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/activate", { token, password: pwd });
      setDone(true);
      toast.success("Compte activé ! Connectez-vous maintenant.");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <Seo title={t("comp_activateaccount_90")} path="/activate" />
      <section className="mx-auto max-w-md px-6 py-16">
        {state.loading ? (
          <div className="text-center text-neutral-500">{t("comp_activateaccount_91")}</div>
        ) : !state.valid ? (
          <div className="rounded-3xl border-2 border-rose-200 bg-rose-50/40 p-8 text-center" data-testid="activate-invalid">
            <AlertCircle className="mx-auto h-12 w-12 text-rose-600" />
            <h2 className="mt-3 text-2xl font-bold text-rose-800">Lien invalide</h2>
            <p className="mt-2 text-sm text-rose-700">{state.reason}</p>
            <a href="mailto:contact@lawinetech.com" className="mt-4 inline-block rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white">{t("comp_activateaccount_92")}</a>
          </div>
        ) : done ? (
          <div className="rounded-3xl border-2 border-emerald-200 bg-emerald-50/40 p-8 text-center" data-testid="activate-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h2 className="mt-3 text-2xl font-bold text-emerald-800">{t("comp_activateaccount_93")}</h2>
            <p className="mt-2 text-neutral-700">{t("comp_activateaccount_94")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl border border-neutral-100 bg-white p-6 shadow-sm" data-testid="activate-form">
            <div className="text-center">
              <KeyRound className="mx-auto h-10 w-10 text-wine-600" />
              <h1 className="mt-3 font-serif text-3xl text-navy">{t("comp_activateaccount_95")}</h1>
              <p className="mt-1 text-sm text-neutral-600">Bienvenue {state.name || state.email} 👋</p>
              <p className="mt-1 text-xs text-neutral-500">{t("comp_activateaccount_96")}</p>
            </div>
            <div className="mt-6 space-y-3">
              <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={t("comp_activateaccount_97")} minLength={6} required className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="activate-password" />
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("comp_activateaccount_98")} minLength={6} required className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="activate-confirm" />
              <button type="submit" disabled={busy} className="w-full rounded-full bg-wine-600 px-5 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="activate-submit">{busy ? "Activation…" : "Activer mon compte"}</button>
            </div>
          </form>
        )}
      </section>
    </PublicLayout>
  );
}
